import { useMediaQuery } from "@mui/system";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import React from "react";
import {
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  List,
  ReferenceField,
  SimpleList,
  TextField,
  TopToolbar,
} from "react-admin";
import { checkPermission } from "server/security/utils";
import { MOBILE_BREAKPOINT, SEMESTERS } from "utils/constants";
import AcademicSessionsListFilters from "../Sessions/ListFilters";
import { settings } from "app";
import { EmptyList } from "server/components";
import SecuredComponent from "server/security/SecuredComponent";
import { SessionExporter } from "../XlsxExporter";
import { ExportXlsButton } from "features/ExportXlsxButton";
import { MobileListRow } from "features/MobileListRow";
import { tenants } from "utils/tenants";
import { administrationRequests } from "api/administrationApi";

const ListAcademicSemesters = ({ onlySemesters }) => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const UserPermissions = getPermissions();

  const ListActions = hasCreate => {
    return (
      <TopToolbar>
        {hasCreate && <CreateButton />}
        <ExportXlsButton exporter={SessionExporter(SEMESTERS)} />
      </TopToolbar>
    );
  };

  return (
    <List
      title="Академични семестри"
      actions={<ListActions hasCreate={checkPermission(UserPermissions, permissions.canCreateAcademicSessions)} />}
      filters={<AcademicSessionsListFilters />}
      filter={{ onlySemesters: onlySemesters }}
    >
      {isMobile ? (
        <SimpleList
          empty={<EmptyList />}
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => <MobileListRow fields={[{ label: "Тип", value: record.typeText }]} />}
          secondaryText={record => (
            <div>
              <MobileListRow fields={[{ label: "Стартира на", value: record.startsOnFormatted }]} />
              <MobileListRow fields={[{ label: "Академична година", value: record.academicYearName }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Вид на обучение",
                    value: (
                      <ReferenceField
                        source="studyTypeId"
                        reference={administrationRequests.getStudyTypesSelect()}
                        record={record}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow fields={[{ label: "Приключва на", value: record.endsOnFormatted }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Статус",
                    value:
                      record.startsInDays > 0
                        ? `Предстоящ (след ${record.startsInDays} дни)`
                        : record.endsInDays > 0
                          ? `Текущ (остават ${record.endsInDays} дни)`
                          : "Завършен",
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Структурна единица",
                    value: (
                      <ReferenceField
                        source="structuralUnitId"
                        reference={administrationRequests.getStructuralUnitsSelect()}
                        record={record}
                      >
                        <TextField source="name" />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              {settings.tenant === tenants.MU && (
                <MobileListRow
                  fields={[
                    {
                      label: "Език на обучение",
                      value: (
                        <ReferenceField
                          source="instructionLanguageId"
                          reference={administrationRequests.getInstructionLanguagesSelect()}
                          record={record}
                        >
                          <TextField source="name" />
                        </ReferenceField>
                      ),
                    },
                  ]}
                />
              )}
            </div>
          )}
          tertiaryText={() => (
            <SecuredComponent permission={permissions.canUpdateAcademicSessions}>
              <EditButton label="" />
            </SecuredComponent>
          )}
        />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
        >
          <TextField
            source="typeText"
            label="Тип"
            sortable={false}
          />

          <TextField
            source="academicYearName"
            label="Академична година"
            sortable={false}
          />
          <ReferenceField
            source="studyTypeId"
            reference={administrationRequests.getStudyTypesSelect()}
            label="Вид на обучение"
          >
            <TextField source="name" />
          </ReferenceField>

          <TextField
            label="Стартира на"
            source="startsOnFormatted"
            sortable={false}
          />

          <TextField
            label="Приключва на"
            source="endsOnFormatted"
            sortable={false}
          />

          <FunctionField
            label="Статус"
            render={record =>
              record.startsInDays > 0
                ? `Предстоящ (след ${record.startsInDays} дни)`
                : record.endsInDays > 0
                  ? `Текущ (остават ${record.endsInDays} дни)`
                  : "Завършен"
            }
          />

          <ReferenceField
            source="structuralUnitId"
            reference={administrationRequests.getStructuralUnitsSelect()}
            label="Структурна единица"
          >
            <TextField source="name" />
          </ReferenceField>
          {settings.tenant === tenants.MU && (
            <ReferenceField
              source="instructionLanguageId"
              reference={administrationRequests.getInstructionLanguagesSelect()}
              label="Език на обучение"
            >
              <TextField source="name" />
            </ReferenceField>
          )}

          <SecuredComponent permission={permissions.canUpdateAcademicSessions}>
            <EditButton />
          </SecuredComponent>
        </Datagrid>
      )}
    </List>
  );
};

export default ListAcademicSemesters;
